import React, { ChangeEvent, FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Theme
} from '@material-ui/core';

import UPDATE_AZSAPP_CLASSROOM_EXPOSE_LETTER_BOOKS_SETTING from 'graph/settings/azsapp/update-azsapp-classroom-expose-letter-books-setting-mutation';
import GET_AZSAPP_CLASSROOM_SETTINGS from 'graph/settings/azsapp/get-azsapp-classroom-settings-query';
import useStore from 'store';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import { Classroom } from 'types/schema-generated-types';
import { useMutation } from '@apollo/client';
import { updateCache } from 'graph/update-cache';

const getClassroomAzsappSettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_AZSAPP_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateAzsappClassroomSettingExposeLetterBooks }
      ): { azsappClassroomSettings: any } => {
        const [{ value }] = updateAzsappClassroomSettingExposeLetterBooks;
        return {
          azsappClassroomSettings: {
            ...cachedData?.azsappClassroomSettings,
            accessBookAfterAssignment: value
          }
        };
      }
    }
  ])
});

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const exposeLetterBooksOptions: {
  label: string;
  value: boolean;
}[] = [
  {
    label: 'Allow access to Letter Books from beginning of year',
    value: false
  },
  {
    label: 'Allow access to Letter Books after assignment',
    value: true
  }
];

interface ExposeLetterBooksSettingProps {
  classroom: Classroom;
  expanded: string | null;
  setting: any;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const ExposeLetterBooksSetting: FC<ExposeLetterBooksSettingProps> = ({
  classroom,
  expanded,
  setting,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('ExposeLetterBooksSetting');
  const classes = useStyles();

  const { id: classroomId } = classroom;
  const [, { openAlert }] = useStore();
  const [updateAzsappClassroomSettingExposeLetterBooks] = useMutation(
    UPDATE_AZSAPP_CLASSROOM_EXPOSE_LETTER_BOOKS_SETTING,
    getClassroomAzsappSettingsMutationCacheUpdate(+classroomId)
  );

  const [enableAccessBookAfterAssignment, setEnableAccessBookAfterAssignment] = useState(
    setting?.enabled || false
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEnableAccessBookAfterAssignment((event.target as HTMLInputElement).value === 'true');
  };

  const handleOnCancel = (): void => {
    setEnableAccessBookAfterAssignment(setting?.enabled || false);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateAzsappClassroomSettingExposeLetterBooks({
        variables: {
          data: {
            classroomId,
            value: {
              enabled: enableAccessBookAfterAssignment
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Expose Letter Books', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Expose Letter Books')}
      panel="exposeLetterBooks"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t(
        'Allow students to have access to all the Letter Books from the beginning of the year or only after teaching the letter in class.'
      )}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <RadioGroup
          aria-label="expose letter books setting"
          name="exposeLetterBooksSetting"
          value={enableAccessBookAfterAssignment}
          onChange={handleChange}
        >
          {exposeLetterBooksOptions.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`expose-letter-books-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default ExposeLetterBooksSetting;
